import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calculator, GitCompareArrows, CalendarDays, BarChart3, Clock, FolderOpen,
  Briefcase, Home, User, LogOut, Sparkles, Building2, FileText, Zap,
  Plus, ChevronRight, ChevronLeft, Loader2,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './ui/command';
import { useAuth } from '../context/AuthContext';
import { useCommandPalette } from '../context/CommandPaletteContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Global ⌘K / Ctrl+K command palette mounted on every authenticated route
 * via AppShell. Provides navigation + recent-estimate + sign-out actions
 * always, and renders any extra groups registered by the active page
 * (e.g., the Estimator's per-component schedule overrides on Step 4).
 */
export default function GlobalCommandPalette() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    open, setOpen,
    groups: extraGroups,
    currentStep, pushStep, popStep,
  } = useCommandPalette();
  const [recentEstimates, setRecentEstimates] = useState([]);
  const [stepItems, setStepItems] = useState(null);
  const [stepLoading, setStepLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Clear the input whenever the user enters or exits a sub-step so
  // the previous level's filter doesn't leak into the new context.
  useEffect(() => {
    setSearchValue('');
  }, [currentStep]);

  // Also clear on close so the palette opens fresh next time.
  useEffect(() => {
    if (!open) setSearchValue('');
  }, [open]);

  // ⌘K / Ctrl+K binding — also handle Backspace at root of palette to
  // pop the current sub-step when one is active.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  // When a sub-step is pushed, lazy-load its items if it ships a
  // `loadItems()` async function. Static `items` are used directly.
  useEffect(() => {
    if (!currentStep) {
      setStepItems(null);
      setStepLoading(false);
      return;
    }
    if (Array.isArray(currentStep.items)) {
      setStepItems(currentStep.items);
      return;
    }
    if (typeof currentStep.loadItems === 'function') {
      let cancelled = false;
      setStepLoading(true);
      Promise.resolve(currentStep.loadItems())
        .then((items) => { if (!cancelled) setStepItems(items || []); })
        .catch(() => { if (!cancelled) setStepItems([]); })
        .finally(() => { if (!cancelled) setStepLoading(false); });
      return () => { cancelled = true; };
    }
    setStepItems([]);
  }, [currentStep]);

  // Lazy-load the user's personalized "recently opened" feed on first
  // open. Falls back to the global recent-estimates list if the user
  // hasn't opened any workspaces yet (empty result).
  useEffect(() => {
    if (!open || recentEstimates.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/estimates/recent`, { params: { limit: 8 } });
        const list = r.data?.estimates || [];
        if (list.length > 0) {
          if (!cancelled) setRecentEstimates(list);
          return;
        }
        // Cold-start fallback — no per-user views yet, show the latest globally.
        const r2 = await axios.get(`${API_URL}/api/estimates`, { params: { limit: 8 } });
        const list2 = Array.isArray(r2.data) ? r2.data : (r2.data?.estimates || []);
        if (!cancelled) setRecentEstimates(list2.slice(0, 8));
      } catch { /* silent — palette still works without recents */ }
    })();
    return () => { cancelled = true; };
  }, [open, recentEstimates.length]);

  const isAdmin = ['admin', 'cs', 'cro', 'cco', 'super_admin'].includes(user?.role);

  const go = (path) => {
    setOpen(false);
    setTimeout(() => navigate(path), 60);
  };

  const NAV_ITEMS = [
    { id: 'nav-home', icon: Home, label: 'Home', path: '/' },
    { id: 'nav-estimator', icon: Calculator, label: 'Estimator', path: '/estimator' },
    { id: 'nav-compare', icon: GitCompareArrows, label: 'Compare', path: '/compare' },
    { id: 'nav-planner', icon: CalendarDays, label: 'Planner', path: '/planner' },
    { id: 'nav-quotes', icon: Briefcase, label: 'Quote Hub', path: '/quotes' },
    { id: 'nav-insights', icon: BarChart3, label: 'Insights', path: '/insights' },
    { id: 'nav-history', icon: Clock, label: 'History', path: '/history' },
    { id: 'nav-workspaces', icon: FolderOpen, label: 'Workspaces', path: '/workspaces' },
    { id: 'nav-quick', icon: Zap, label: 'Quick Estimate', path: '/quick-estimate' },
  ];

  const QUICK_ACTIONS = [
    { id: 'qa-new-estimate', icon: Plus, label: 'New estimate for…', action: () => {
      pushStep({
        id: 'pick-org-for-new-estimate',
        label: 'New estimate for',
        placeholder: 'Pick an organization…',
        loadItems: async () => {
          const r = await axios.get(`${API_URL}/api/organizations`);
          const orgs = r.data?.organizations || r.data || [];
          return orgs.map((org) => ({
            id: `org-${org.org_id}`,
            value: `${org.name || ''} ${org.industry || ''} ${org.region || ''}`,
            icon: Building2,
            label: org.name || 'Unnamed organization',
            sublabel: [org.industry, org.region].filter(Boolean).join(' · '),
            onSelect: () => {
              setOpen(false);
              setTimeout(() => navigate(`/estimator/new?org_id=${org.org_id}`), 60);
            },
          }));
        },
      });
    } },
    { id: 'qa-profile', icon: User, label: 'Open profile & preferences', path: '/profile' },
    ...(isAdmin
      ? [
          { id: 'qa-admin', icon: Building2, label: 'Admin — Organizations & Users', path: '/admin?tab=organizations' },
          { id: 'qa-sow-new', icon: FileText, label: 'Create new SOW', path: '/sow/new' },
        ]
      : []),
    { id: 'qa-tour', icon: Sparkles, label: 'Restart onboarding tour', action: () => {
      try { localStorage.removeItem('onboarding_completed'); } catch { /* ignore */ }
      window.location.reload();
    } },
    { id: 'qa-logout', icon: LogOut, label: 'Sign out', action: () => { setOpen(false); logout(); } },
  ];

  return (
    <CommandDialog open={open} onOpenChange={setOpen} data-testid="global-command-palette">
      {currentStep && (
        <button
          type="button"
          onClick={popStep}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-500 hover:text-[#0F2040] border-b border-slate-100 hover:bg-slate-50 transition-colors"
          data-testid="cmdk-step-back"
        >
          <ChevronLeft className="w-3 h-3" />
          <span className="text-slate-300">{currentStep.label}</span>
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <span className="text-[#0F2040]">{currentStep.placeholder || 'Pick one…'}</span>
        </button>
      )}
      {/* Controlled input value so we can clear the filter when transitioning
          between root ↔ sub-step (cmdk doesn't auto-clear on its own). */}
      <CommandInput
        value={searchValue}
        onValueChange={setSearchValue}
        placeholder={currentStep ? (currentStep.placeholder || 'Pick one…') : 'Type to search — go to, recent estimates, actions…'}
        data-testid="global-command-input"
      />
      <CommandList className="max-h-[480px]">
        <CommandEmpty>{stepLoading ? 'Loading…' : 'No matches.'}</CommandEmpty>

        {currentStep ? (
          <>
            {stepLoading && (
              <div className="px-4 py-6 flex items-center justify-center text-slate-400 text-xs gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading {currentStep.label.toLowerCase()}…
              </div>
            )}
            {stepItems && stepItems.length > 0 && (
              <CommandGroup heading={currentStep.heading || currentStep.label}>
                {stepItems.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.value}
                    onSelect={() => {
                      if (item.onSelect) item.onSelect();
                    }}
                    data-testid={item.testId || `cmdk-step-${item.id}`}
                  >
                    {item.icon ? <item.icon className="text-slate-400" /> : null}
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm truncate">{item.label}</span>
                      {item.sublabel && (
                        <span className="text-[10px] text-slate-400 truncate">{item.sublabel}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        ) : (
          <>
            {/* Page-registered groups render first so context-relevant commands
                sit at the top when the user opens the palette on a deep page. */}
            {extraGroups.map((g) => (
              <CommandGroup key={g.key} heading={g.heading}>
                {(g.items || []).map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.value}
                    onSelect={() => {
                      if (item.onSelect) item.onSelect();
                      if (item.closeOnSelect !== false) setOpen(false);
                    }}
                    className={item.className || ''}
                    data-testid={item.testId || `cmdk-extra-${item.id}`}
                  >
                    {item.render ? item.render() : (
                      <>
                        {item.icon ? <item.icon className="text-slate-400" /> : null}
                        <span className="text-sm">{item.label}</span>
                      </>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}

            <CommandGroup heading="Go to">
              {NAV_ITEMS.map((n) => (
                <CommandItem
                  key={n.id}
                  value={`${n.label} go to navigate`}
                  onSelect={() => go(n.path)}
                  data-testid={`cmdk-${n.id}`}
                >
                  <n.icon className="text-slate-400" />
                  <span className="text-sm">{n.label}</span>
                  <span className="ml-auto text-[10px] text-slate-300 font-mono">{n.path}</span>
                </CommandItem>
              ))}
            </CommandGroup>

            {recentEstimates.length > 0 && (
              <CommandGroup heading="Recent estimates">
                {recentEstimates.map((est) => (
                  <CommandItem
                    key={est.id}
                    value={`${est.estimate_name || ''} ${est.organization_name || ''} ${est.implementation_type || ''} ${est.id}`}
                    onSelect={() => go(`/workspace/${est.id}/overview`)}
                    data-testid={`cmdk-est-${est.id}`}
                  >
                    <FileText className="text-slate-400" />
                    <span className="text-sm truncate">{est.estimate_name || est.organization_name || 'Untitled estimate'}</span>
                    {est.organization_name && (
                      <span className="ml-auto text-[10px] text-slate-400 truncate max-w-[160px]">
                        {est.organization_name}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            <CommandGroup heading="Actions">
              {QUICK_ACTIONS.map((a) => (
                <CommandItem
                  key={a.id}
                  value={a.label}
                  onSelect={() => {
                    if (a.action) a.action();
                    else if (a.path) go(a.path);
                  }}
                  data-testid={`cmdk-${a.id}`}
                >
                  <a.icon className="text-slate-400" />
                  <span className="text-sm">{a.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>

      <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between text-[10px] text-slate-400">
        <span>
          <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">↑</kbd>
          <kbd className="px-1.5 py-0.5 bg-slate-100 rounded ml-1">↓</kbd> navigate
          <span className="mx-2">·</span>
          <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">Enter</kbd> select
          <span className="mx-2">·</span>
          {currentStep ? (
            <>
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">←</kbd>
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded ml-1">Back</kbd> step out
            </>
          ) : (
            <>
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">Esc</kbd> close
            </>
          )}
        </span>
        <span className="text-slate-300">⌘K to toggle</span>
      </div>
    </CommandDialog>
  );
}
